import {
  Box,
  Checkbox,
  Collapse,
  Divider,
  Flex,
  // Icon,
  Stack,
  Text,
} from "@chakra-ui/react";
import Pluralize from "pluralize";
import * as React from "react";
import { learningNeeds } from "../utils/data";
import {
  arrayToString,
  possessiveNoun,
  removeClassPrefix,
} from "../utils/functions";
import Icon from "./ui-elements/Icon";

type Props = {
  isInvalid: boolean;
  checkedStatus: any;
  isDisabled?: boolean;
  lostRequest: any;
  isPending?: boolean;
  displayCheckbox?: boolean;
  onChange?: (e: any, index: number) => void;
  students: Array<any>;
  [key: string]: any;
};

type StudentCardProps = {
  displayName: string;
  gender: string;
  isDisabled: boolean;
  isPending: boolean;
  isRead: boolean;
  isInvalid: boolean;
  lostRequest: any;
  classDetail: any;
  learningNeed: any;
  curriculum: Array<any>;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  expectation: any;
  special_needs: any;
  displayCheckbox?: boolean;
  [key: string]: any;
};

const BookingDetails = ({
  isInvalid,
  checkedStatus = () => {},
  isDisabled,
  lostRequest,
  isPending,
  onChange = () => {},
  students = [],
  displayCheckbox = true,
  ...rest
}: Props) => {
  return (
    <Stack spacing={[5, 6]} px={[5, 6]} {...rest}>
      <Stack spacing={1}>
        <Text fontSize="lg" fontWeight="medium">
          Booking request for {Pluralize("student", students.length, true)}
        </Text>
        <Text opacity={0.7}>Review each student's learning needs</Text>
      </Stack>

      <Stack spacing={3}>
        {students.map((student, idx) => {
          return (
            <StudentCard
              key={idx}
              {...student}
              displayName={student.name}
              displayCheckbox={displayCheckbox}
              isRead={isDisabled ? true : checkedStatus[idx]}
              isDisabled={isDisabled}
              lostRequest={lostRequest}
              isPending={isPending}
              onChange={(e) => onChange(e, idx)}
              isInvalid={isInvalid && !checkedStatus[idx]}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};

const StudentCard = ({
  displayName,
  gender,
  isDisabled,
  isPending,
  isRead,
  isInvalid,
  lostRequest,
  classDetail: { class: studentClass, purpose, subjects },
  learningNeed,
  curriculum = [],
  onChange = () => {},
  expectation,
  special_needs,
  displayCheckbox = true,
  ...rest
}: StudentCardProps) => {
  const [show, setShow] = React.useState(false);
  const lessonSubjects = subjects.map((subject: string) =>
    removeClassPrefix(subject),
  );
  const painPoint = learningNeeds(gender === "male").find(
    (need) => need.value === learningNeed,
  );

  const handleToggle = () => !lostRequest && setShow(!show);

  return (
    <Box
      borderWidth="2xs"
      borderRadius="fbRadius"
      borderColor={isInvalid ? "red.500" : "inherit"}
      outline="blue.300"
      width="100%"
      bg="neutral.50"
      _hover={{ borderColor: isInvalid ? "red.500" : "gray.300" }}
      {...rest}
    >
      <Box>
        <Box cursor="pointer">
          <Flex
            p={[5, 6]}
            justify="space-between"
            align="center"
            onClick={handleToggle}
          >
            <Stack spacing={5} isInline mr={[6]}>
              <Icon name={gender} size={6} display={["none", "block"]} />
              <Box mt={-1}>
                <Text fontWeight={show ? "medium" : "inherit"}>
                  {displayName}, {studentClass}
                </Text>

                <Text opacity={0.8}>{purpose}</Text>

                {isInvalid && (
                  <Stack isInline align="center" mt={2} color="red.500">
                    <Icon name="error" size={4} />
                    <Text fontSize="sm">Please review student's need</Text>
                  </Stack>
                )}
              </Box>
            </Stack>

            <Stack isInline align="center">
              {!show && isRead && (
                <Icon name="check" color="blue.500" size={6} />
              )}
              <Icon name={show ? "chevron-up" : "chevron-down"} />
            </Stack>
          </Flex>
        </Box>

        <Collapse in={show}>
          <Stack px={[5, 6]} pb={[5, 6]} spacing={[5, 6]}>
            <Stack isInline spacing={5}>
              <Icon name="star-trophy" size={6} display={["none", "block"]} />
              <Box mt={[0, -1]}>
                <Text fontWeight="medium">Lesson goal</Text>

                <Text opacity={0.8}>{expectation}</Text>
              </Box>
            </Stack>

            <Stack isInline spacing={5}>
              <Icon name="bullseye" size={6} display={["none", "block"]} />
              <Box mt={[0, -1]}>
                <Text fontWeight="medium">Pain point: {painPoint?.value}</Text>

                <Text opacity={0.8}>{painPoint?.description}</Text>
              </Box>
            </Stack>

            <Stack isInline spacing={5}>
              <Icon name="education" size={6} display={["none", "block"]} />
              <Box mt={[0, -1]}>
                <Text fontWeight="medium">Subjects you'll teach</Text>

                <Text opacity={0.8}>{arrayToString(lessonSubjects)}</Text>
              </Box>
            </Stack>

            {curriculum.length > 0 && (
              <Stack isInline spacing={5}>
                <Icon name="school" size={6} display={["none", "block"]} />
                <Box mt={[0, -1]}>
                  <Text fontWeight="medium">Curriculum used in school</Text>
                  {curriculum.length > 1 && (
                    <Text opacity={0.8}>{arrayToString(curriculum)}</Text>
                  )}
                  {curriculum.length === 1 && (
                    <Text opacity={0.8}>
                      Strictly {arrayToString(curriculum)}
                    </Text>
                  )}
                </Box>
              </Stack>
            )}

            {special_needs && special_needs !== "None" && (
              <Stack isInline spacing={5}>
                <Icon name="care" size={6} display={["none", "block"]} />
                <Box mt={[0, -1]}>
                  <Text fontWeight="medium">Special learning need</Text>

                  <Text opacity={0.8}>
                    Requires experience handling kids with {special_needs}
                  </Text>
                </Box>
              </Stack>
            )}

            {displayCheckbox && (
              <>
                <Divider />
                <Checkbox
                  size="lg"
                  borderColor="rgb(227, 231, 237)"
                  // alignSelf="center"
                  alignItems="baseline"
                  defaultIsChecked={isDisabled}
                  isDisabled={isDisabled || !isPending}
                  onChange={onChange}
                >
                  <Text
                    fontSize="md"
                    color={isDisabled ? "inherit" : "blue.500"}
                  >
                    I've read and understand {possessiveNoun(displayName)} needs
                  </Text>
                </Checkbox>
              </>
            )}
          </Stack>
        </Collapse>
      </Box>
    </Box>
  );
};

export default BookingDetails;
